class ProductInfoContent extends HTMLElement {
  connectedCallback() {
    this.column = this.closest('.shopify-section--main-product .product-info');
    if (!this.column) return;
    this.desktop = window.matchMedia('(min-width: 1000px)');
    this.observer = new ResizeObserver(this.updateSpacing);
    this.observer.observe(this);
    this.observer.observe(this.column);
    this.addEventListener('accordion:after-open', this.onOpened);
    this.updateSpacing();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('accordion:after-open', this.onOpened);
    cancelAnimationFrame(this.revealFrame);
  }

  updateSpacing = () => {
    if (!this.desktop.matches) {
      this.style.removeProperty('--product-panel-space');
      return;
    }
    const styles = getComputedStyle(this);
    let closedHeight = this.getBoundingClientRect().height - parseFloat(styles.paddingTop) - parseFloat(styles.paddingBottom);
    this.querySelectorAll('.product-detail-panels > accordion-disclosure > details[open]').forEach((details) => {
      closedHeight -= details.getBoundingClientRect().height - details.querySelector('summary').getBoundingClientRect().height;
    });
    const minimum = parseFloat(getComputedStyle(this.column).scrollPaddingTop) || 24;
    const centeredSpace = this.column.closest('.product--without-media') ? 0 : (this.column.clientHeight - closedHeight) / 2;
    const space = `${Math.max(minimum, centeredSpace).toFixed(2)}px`;
    if (this.style.getPropertyValue('--product-panel-space') !== space) {
      this.style.setProperty('--product-panel-space', space);
    }
  };

  onOpened = (event) => {
    const accordion = event.target;
    if (!accordion.matches('.product-detail-panels > accordion-disclosure')) return;
    cancelAnimationFrame(this.revealFrame);
    this.revealFrame = requestAnimationFrame(() => {
      if (!this.isConnected || !accordion.classList.contains('is-open')) return;
      this.updateSpacing();
      const bounds = accordion.getBoundingClientRect();
      const header = document.querySelector('.shopify-section--header')?.getBoundingClientRect();
      let top = Math.max(0, header?.bottom || 0);
      let bottom = window.innerHeight;
      if (this.desktop.matches) {
        const columnBounds = this.column.getBoundingClientRect();
        top = Math.max(top, columnBounds.top);
        bottom = Math.min(bottom, columnBounds.bottom);
      }
      const gap = 24;
      top += gap;
      bottom -= gap;
      if (bottom <= top) return;
      const delta = bounds.height > bottom - top || bounds.top < top
        ? bounds.top - top
        : Math.max(0, bounds.bottom - bottom);
      if (Math.abs(delta) < 1) return;
      const scroller = this.desktop.matches ? this.column : window;
      scroller.scrollBy({
        top: delta,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
    });
  };
}

if (!window.customElements.get('product-info-content')) {
  window.customElements.define('product-info-content', ProductInfoContent);
}

class ColorwayRun extends HTMLElement {
  connectedCallback() {
    this.caption = this.closest('.product-info')?.querySelector('.product-title__color');
    this.restingLabel = this.caption?.textContent.trim() || '';
    this.previews = this.readPreviews();

    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('pointerout', this.onRest);
    this.addEventListener('focusin', this.onPointerOver);
    this.addEventListener('focusout', this.onRest);
  }

  disconnectedCallback() {
    this.onRest();
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('pointerout', this.onRest);
    this.removeEventListener('focusin', this.onPointerOver);
    this.removeEventListener('focusout', this.onRest);
  }

  readPreviews() {
    try {
      const urls = JSON.parse(this.dataset.previewUrls || '[]');
      const images = JSON.parse(this.dataset.previewImages || '[]');
      return Object.fromEntries(urls.map((url, index) => [url, images[index]]).filter(([, src]) => src));
    } catch {
      return {};
    }
  }

  /*
   * The overlay is created on the first hover rather than rendered by Liquid, because it exists only
   * to be faded and a page nobody hovers should not carry it. It is inserted INTO the first gallery
   * frame so it inherits that frame's box exactly, whatever the gallery's layout is doing.
   */
  overlay() {
    if (this.previewLayer?.isConnected) return this.previewLayer;

    const frame = document.querySelector('.product-gallery__media');
    if (!frame) return null;

    const layer = document.createElement('img');
    layer.className = 'product-gallery__colorway-preview';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    frame.appendChild(layer);
    /* Commit the layer's resting style (opacity 0) before anything shows it. Created and shown in
       one style pass, the first reveal on a page had no start value to transition from and popped
       in at full strength; every later reveal faded. One layout read, once per page. */
    void layer.offsetWidth;
    this.previewLayer = layer;
    return layer;
  }

  showPreview(href) {
    const src = this.previews[href];
    if (!src) {
      this.hidePreview();
      return;
    }

    const layer = this.overlay();
    if (!layer) return;

    if (layer.getAttribute('src') !== src) layer.setAttribute('src', src);

    const token = (this.previewToken = (this.previewToken || 0) + 1);
    const reveal = () => {
      if (this.previewToken !== token) return;
      layer.classList.add('is-shown');
    };
    if (layer.complete && layer.naturalWidth > 0) reveal();
    else layer.decode().then(reveal, reveal);
  }

  hidePreview() {
    this.previewToken = (this.previewToken || 0) + 1;
    this.previewLayer?.classList.remove('is-shown');
  }

  onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    const link = event.target.closest('a');
    if (!link || !this.contains(link)) return;

    const label = link.querySelector('.sr-only')?.textContent.trim() || link.textContent.trim();
    if (label && this.caption) this.caption.textContent = label;

    this.showPreview(link.getAttribute('href'));
  };

  onRest = (event) => {
    if (event?.type === 'focusout' && this.contains(event.relatedTarget)) return;
    if (event?.type === 'pointerout' && event.target.closest('a')?.contains(event.relatedTarget)) return;
    if (this.caption) this.caption.textContent = this.restingLabel;
    this.hidePreview();
  };
}

if (!window.customElements.get('colorway-run')) {
  window.customElements.define('colorway-run', ColorwayRun);
}

const ADDED_LABEL_MS = 1200;
const PULSE_MS = 200;

class BuyGate extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button[type="submit"]');
    if (!this.button) return;

    this.panel = this.closest('.product-info');

    this.sizeList = this.panel?.querySelector('.variant-picker__option-values[data-value-count]');
    this.armed = false;
    this.acknowledging = false;

    this.addEventListener('variant:add', this.onAdded);

    this.addEventListener('click', this.onGhostPress, true);
    this.addEventListener('submit', this.onGhostPress, true);

    // The label is the only thing this element writes into the button, so it is the only thing that
    // has to be put back after a swap.
    this.observer = new MutationObserver(() => this.applyLabel());
    this.observer.observe(this, { childList: true, subtree: true });

    if (!this.canArm()) return;

    this.arm();
    this.panel?.addEventListener('change', this.onChoice);
  }

  disconnectedCallback() {
    this.removeEventListener('variant:add', this.onAdded);
    this.removeEventListener('click', this.onGhostPress, true);
    this.removeEventListener('submit', this.onGhostPress, true);
    this.panel?.removeEventListener('change', this.onChoice);
    this.observer?.disconnect();
    clearTimeout(this.labelTimer);
    clearTimeout(this.pulseTimer);
  }

  /*
   * The label for the state the element is in, applied to whatever button is in the document now.
   * Called on every state change and after every re-render, so the two can never disagree.
   */
  applyLabel() {
    const button = this.querySelector('button[type="submit"]');
    if (!button) return;
    const target = button.querySelector('.button__content') || button;

    let text = this.dataset.addLabel;
    if (this.acknowledging) text = this.dataset.addedLabel;
    else if (this.armed) text = this.dataset.selectLabel;

    if (text && target.textContent.trim() !== text) target.textContent = text;
  }

  canArm() {
    // Nothing to choose: one value in the run, or no run at all.
    if (!this.sizeList || this.sizeList.querySelectorAll('input[type="radio"]').length < 2) return false;
    // Nothing to add: the button is already saying sold out or unavailable.
    if (this.button.disabled) return false;
    // Already chosen: the URL names a variant, which is what a shared or reloaded selection looks like.
    if (new URLSearchParams(window.location.search).has('variant')) return false;
    return true;
  }

  arm() {
    this.armed = true;
    this.classList.add('buy-gate--unselected');
    this.applyLabel();
    this.sizeList.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      input.checked = false;
    });
  }

  onChoice = (event) => {
    if (!this.armed) return;
    if (!event.target.closest('.variant-picker__option-values')) return;
    this.armed = false;
    this.classList.remove('buy-gate--unselected');
    this.applyLabel();
  };

  onGhostPress = (event) => {
    if (!this.armed) return;
    // Recipient fields remain usable before the gift-card amount is selected.
    if (event.type === 'click' && !event.target.closest('button[type="submit"]')) return;
    event.preventDefault();
    event.stopPropagation();
    this.pulse();
  };

  pulse() {
    this.sizeList.classList.remove('is-pulsing');
    // Reading a layout value between the remove and the add is what restarts the animation when the
    // ghost is pressed twice; without it the class is never absent for a frame and nothing replays.
    void this.sizeList.offsetWidth;
    this.sizeList.classList.add('is-pulsing');
    clearTimeout(this.pulseTimer);
    this.pulseTimer = setTimeout(() => this.sizeList.classList.remove('is-pulsing'), PULSE_MS);
  }

  onAdded = () => {
    this.acknowledging = true;
    this.classList.add('buy-gate--added');
    this.applyLabel();
    clearTimeout(this.labelTimer);
    this.labelTimer = setTimeout(() => {
      this.acknowledging = false;
      this.classList.remove('buy-gate--added');
      this.applyLabel();
    }, ADDED_LABEL_MS);
  };
}

if (!window.customElements.get('buy-gate')) {
  window.customElements.define('buy-gate', BuyGate);
}

(() => {
  const column = document.querySelector('.shopify-section--main-product .product-info');
  const gallery = document.querySelector('.shopify-section--main-product .product-gallery');
  if (!column || !gallery) return;
  if (!window.matchMedia('(pointer: fine)').matches) return;
  gallery.dataset.scrollHandoff = 'bound';
  gallery.addEventListener('wheel', (event) => {
    if (event.ctrlKey) return;
    const overflow = column.scrollHeight - column.clientHeight;
    if (overflow <= 1) return;
    const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
    const atTop = column.scrollTop <= 0;
    const atEnd = column.scrollTop >= overflow - 1;
    if ((delta > 0 && atEnd) || (delta < 0 && atTop) || delta === 0) return;
    column.scrollTop += delta;
    event.preventDefault();
  }, { passive: false });
})();
